/**
 * Unity WebGL Preloader Service
 * Preloads Unity game assets in the background after user login
 * so they're cached and ready when the user navigates to game screens.
 *
 * Features:
 * - Deferred start: waits for browser idle time before starting
 * - Low priority: uses fetch priority hints to not compete with UI
 * - Throttled: adds delays between files to keep UI responsive
 * - Abortable: can be cancelled on logout or navigation
 */

import { UNITY_BUILD_BASE, UNITY_PRELOAD_FILES } from '../config/unityBuild';

interface PreloadProgress {
  loaded: number;
  total: number;
  percentage: number;
  currentFile: string;
}

type ProgressCallback = (progress: PreloadProgress) => void;

// Polyfill for requestIdleCallback
const requestIdleCallback =
  (window as any).requestIdleCallback ||
  ((cb: (deadline: { timeRemaining: () => number }) => void) =>
    setTimeout(() => cb({ timeRemaining: () => 50 }), 1));

const cancelIdleCallback =
  (window as any).cancelIdleCallback ||
  ((id: number) => clearTimeout(id));

class UnityPreloaderService {
  private isPreloading = false;
  private preloadPromise: Promise<void> | null = null;
  private progressCallbacks: Set<ProgressCallback> = new Set();
  private abortController: AbortController | null = null;
  private idleCallbackId: number | null = null;
  private startDelayTimeout: ReturnType<typeof setTimeout> | null = null;

  // Cache status
  private cachedFiles: Set<string> = new Set();
  private totalBytesLoaded = 0;
  private totalBytesToLoad = 0;

  // Configuration
  private readonly PRELOAD_DELAY_MS = 3000; // Wait 3s after login before starting
  private readonly CHUNK_DELAY_MS = 100; // Pause between file downloads
  private readonly IDLE_TIMEOUT_MS = 5000; // Max wait for idle callback

  // Unity build configuration - imported from centralized config
  // Update ../config/unityBuild.ts after running "Build/Build Unified WebGL"
  private readonly unityBuildBase = UNITY_BUILD_BASE;
  private readonly unityBuildFiles = UNITY_PRELOAD_FILES;

  /**
   * Start preloading Unity assets in the background
   * Called after successful login - uses deferred/idle loading to not block UI
   */
  async startPreloading(): Promise<void> {
    // On a room route Unity itself mounts seconds later and fetches these exact
    // URLs — a concurrent preload DOUBLES the 53 MB download (measured: cold
    // mobile room loads pulled ~106 MB). Let the game's own loader do the work.
    if (window.location.pathname.startsWith('/room/')) {
      console.log('[UnityPreloader] Room route — skipping preload (Unity loads directly)');
      return;
    }

    // Respect constrained connections and mobile data: browsing the dashboard
    // must not silently burn ~53 MB of cellular data.
    const nav = navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    };
    if (nav.connection?.saveData ||
        ['slow-2g', '2g', '3g'].includes(nav.connection?.effectiveType ?? '')) {
      console.log('[UnityPreloader] Constrained connection — skipping preload');
      return;
    }
    if (/iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent)) {
      console.log('[UnityPreloader] Mobile device — skipping background preload');
      return;
    }

    if (this.isPreloading) {
      console.log('[UnityPreloader] Already preloading, returning existing promise');
      return this.preloadPromise!;
    }

    // Check if already cached (quick check, doesn't block)
    if (await this.checkCacheStatus()) {
      console.log('[UnityPreloader] Assets already cached, skipping preload');
      this.notifyProgress({
        loaded: 100,
        total: 100,
        percentage: 100,
        currentFile: 'All files cached',
      });
      return;
    }

    this.isPreloading = true;
    this.abortController = new AbortController();

    console.log('[UnityPreloader] Scheduling background preload (will start after UI settles)');

    // Create promise that resolves when preloading completes
    this.preloadPromise = new Promise((resolve) => {
      // Wait for initial delay to let UI render and settle
      this.startDelayTimeout = setTimeout(() => {
        // Then wait for browser idle time
        this.idleCallbackId = requestIdleCallback(
          () => {
            console.log('[UnityPreloader] Browser idle, starting preload');
            this.doPreload().then(resolve).catch(() => resolve());
          },
          { timeout: this.IDLE_TIMEOUT_MS }
        );
      }, this.PRELOAD_DELAY_MS);
    });

    return this.preloadPromise;
  }

  /**
   * Stop preloading (e.g., if user logs out or navigates away)
   */
  stopPreloading(): void {
    // Cancel pending start delay
    if (this.startDelayTimeout) {
      clearTimeout(this.startDelayTimeout);
      this.startDelayTimeout = null;
    }

    // Cancel pending idle callback
    if (this.idleCallbackId !== null) {
      cancelIdleCallback(this.idleCallbackId);
      this.idleCallbackId = null;
    }

    // Abort any in-flight fetches
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.isPreloading = false;
    this.preloadPromise = null;
    console.log('[UnityPreloader] Preloading stopped');
  }

  /**
   * Check if assets are already in browser cache
   */
  private async checkCacheStatus(): Promise<boolean> {
    try {
      // Use Cache API to check if files exist
      const cache = await caches.open('unity-preload-cache');
      const mainFile = this.unityBuildBase + this.unityBuildFiles[3].path; // data.br
      const cached = await cache.match(mainFile);
      return !!cached;
    } catch {
      return false;
    }
  }

  /**
   * Perform the actual preloading
   */
  private async doPreload(): Promise<void> {
    try {
      // Calculate total size
      this.totalBytesToLoad = this.unityBuildFiles.reduce((sum, f) => sum + f.size, 0);
      this.totalBytesLoaded = 0;

      // Open cache for storing responses
      const cache = await caches.open('unity-preload-cache');

      // Preload character editor files sequentially with delays to keep UI responsive
      for (const file of this.unityBuildFiles) {
        if (this.abortController?.signal.aborted) {
          console.log('[UnityPreloader] Preload aborted');
          break;
        }

        const fullPath = this.unityBuildBase + file.path;
        await this.preloadFile(fullPath, file.size, cache);

        // Small delay between files to yield to UI thread
        await this.sleep(this.CHUNK_DELAY_MS);
      }

      console.log('[UnityPreloader] Preload complete!');
      this.notifyProgress({
        loaded: this.totalBytesToLoad,
        total: this.totalBytesToLoad,
        percentage: 100,
        currentFile: 'Complete',
      });

    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log('[UnityPreloader] Preload was aborted');
      } else {
        console.error('[UnityPreloader] Preload error:', error);
      }
    } finally {
      this.isPreloading = false;
    }
  }

  /**
   * Preload a single file with progress tracking
   */
  private async preloadFile(url: string, expectedSize: number, cache: Cache): Promise<void> {
    try {
      // Check if already cached
      const cached = await cache.match(url);
      if (cached) {
        console.log(`[UnityPreloader] ${url} already cached`);
        this.totalBytesLoaded += expectedSize;
        this.cachedFiles.add(url);
        this.notifyProgress({
          loaded: this.totalBytesLoaded,
          total: this.totalBytesToLoad,
          percentage: Math.round((this.totalBytesLoaded / this.totalBytesToLoad) * 100),
          currentFile: url.split('/').pop() || url,
        });
        return;
      }

      console.log(`[UnityPreloader] Preloading: ${url}`);

      const response = await fetch(url, {
        signal: this.abortController?.signal,
        cache: 'default',
        // Low priority so we don't compete with user-initiated requests
        priority: 'low',
      } as RequestInit);

      if (!response.ok) {
        console.warn(`[UnityPreloader] Failed to fetch ${url}: ${response.status}`);
        return;
      }

      // Clone response for caching (response body can only be read once)
      const responseClone = response.clone();

      // Read with progress tracking
      const reader = response.body?.getReader();
      if (reader) {
        let loaded = 0;
        const chunks: Uint8Array[] = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          chunks.push(value);
          loaded += value.length;
          this.totalBytesLoaded += value.length;

          // Notify progress (throttled to every 100KB)
          if (loaded % 102400 < value.length) {
            this.notifyProgress({
              loaded: this.totalBytesLoaded,
              total: this.totalBytesToLoad,
              percentage: Math.round((this.totalBytesLoaded / this.totalBytesToLoad) * 100),
              currentFile: url.split('/').pop() || url,
            });
          }
        }
      }

      // Store in cache
      await cache.put(url, responseClone);
      this.cachedFiles.add(url);
      console.log(`[UnityPreloader] Cached: ${url}`);

    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error(`[UnityPreloader] Error preloading ${url}:`, error);
      }
    }
  }

  // NOTE: the old addressables-catalog preload was removed — the build ships no
  // Addressables, so it produced a guaranteed 403 + console noise on every preload.

  /**
   * Sleep helper for throttling - yields to UI thread
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Subscribe to preload progress
   */
  onProgress(callback: ProgressCallback): () => void {
    this.progressCallbacks.add(callback);
    return () => this.progressCallbacks.delete(callback);
  }

  /**
   * Notify all progress callbacks
   */
  private notifyProgress(progress: PreloadProgress): void {
    this.progressCallbacks.forEach(cb => {
      try {
        cb(progress);
      } catch (e) {
        console.error('[UnityPreloader] Progress callback error:', e);
      }
    });
  }

  /**
   * Get current preload status
   */
  getStatus(): { isPreloading: boolean; cachedFiles: number; progress: number } {
    return {
      isPreloading: this.isPreloading,
      cachedFiles: this.cachedFiles.size,
      progress: this.totalBytesToLoad > 0
        ? Math.round((this.totalBytesLoaded / this.totalBytesToLoad) * 100)
        : 0,
    };
  }
}

// Singleton instance
export const unityPreloader = new UnityPreloaderService();
